import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PLANES } from '../static/Planes';
import { PHYSICS } from '../static/Physics';
import { UPGRADES, MILESTONES, ZONES } from '../static/Const';
import { SoundManager } from '../servcies/SoundManager';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { config } from '../config/config';
import API_URL from '../config/APIURL';
// ============================================
// UTILITY FUNCTIONS
// ============================================

const vec3 = {
  add: (a, b) => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }),
  sub: (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }),
  scale: (v, s) => ({ x: v.x * s, y: v.y * s, z: v.z * s }),
  length: (v) => Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z),
  normalize: (v) => {
    const len = vec3.length(v);
    return len > 0 ? vec3.scale(v, 1 / len) : { x: 0, y: 0, z: 1 };
  },
  lerp: (a, b, t) => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  }),
  dot: (a, b) => a.x * b.x + a.y * b.y + a.z * b.z,
};

const rotate3D = (v, rot) => {
  let { x, y, z } = v;
  
  // Roll (Z-axis)
  const cz = Math.cos(rot.z), sz = Math.sin(rot.z);
  [x, y] = [x * cz - y * sz, x * sz + y * cz];
  
  // Pitch (X-axis)
  const cx = Math.cos(rot.x), sx = Math.sin(rot.x);
  [y, z] = [y * cx - z * sx, y * sx + z * cx];
  
  // Yaw (Y-axis)
  const cy = Math.cos(rot.y), sy = Math.sin(rot.y);
  [x, z] = [x * cy + z * sy, -x * sy + z * cy];
  
  return { x, y, z };
};

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
const lerp = (a, b, t) => a + (b - a) * t;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

// ============================================
// SOUND MANAGER (Web Audio API)
// ============================================


const sounds = new SoundManager();

// ============================================
// GAME STORAGE
// ============================================

const STORAGE_KEY = 'paper_flight_save_v2';

const defaultStats = {
  totalCoins: 100,
  bestDistance: 0,
  totalFlights: 0,
  aerodynamicsLevel: 0,
  boostLevel: 0,
  liftLevel: 0,
  coinsLevel: 0,
  unlockedPlanes: ['classic'],
  selectedPlane: 'classic',
  achievements: [],
  dailyMissions: [],
  lastDaily: null,
};

const loadGame = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? { ...defaultStats, ...JSON.parse(saved) } : defaultStats;
  } catch {
    return defaultStats;
  }
};

const saveGame = (stats) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  } catch {}
};

// ============================================
// PAPER PLANE 3D RENDERER
// ============================================

const PLANE_MODEL = {
  vertices: [
    { x: 0, y: 0, z: 2.5 },      // 0: Nose
    { x: 0, y: 0.3, z: -2 },     // 1: Tail top
    { x: -3, y: 0.3, z: -1.5 },  // 2: Left wing tip
    { x: 3, y: 0.3, z: -1.5 },   // 3: Right wing tip
    { x: 0, y: -0.3, z: -2 },    // 4: Tail bottom
    { x: -1.5, y: 0.2, z: -1 },  // 5: Left wing mid
    { x: 1.5, y: 0.2, z: -1 },   // 6: Right wing mid
  ],
  faces: [
    { indices: [0, 5, 2], shade: 1.0 },    // Left wing top outer
    { indices: [0, 1, 5], shade: 0.95 },   // Left wing top inner
    { indices: [5, 1, 2], shade: 0.85 },   // Left wing back
    { indices: [0, 3, 6], shade: 0.9 },    // Right wing top outer
    { indices: [0, 6, 1], shade: 0.85 },   // Right wing top inner
    { indices: [6, 2, 1], shade: 0.8 },    // Right wing back
    { indices: [0, 2, 4], shade: 0.7 },    // Left underside
    { indices: [0, 4, 3], shade: 0.65 },   // Right underside
    { indices: [2, 1, 4], shade: 0.6 },    // Back left
    { indices: [1, 3, 4], shade: 0.55 },   // Back right
  ],
};

// ============================================
// MAIN GAME COMPONENT
// ============================================

const PaperFlightGame = () => {
  const { user, isAuthenticated, isLoading, verifyAuth } = useAuth();
  const { showToast } = useToast();
  const [authChecked, setAuthChecked] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [gameState, setGameState] = useState('menu'); // menu, playing, shop, gameOver
  const [stats, setStats] = useState(loadGame);
  const [lastRun, setLastRun] = useState({ distance: 0, coins: 0, newBest: false });
  const [hudData, setHudData] = useState({ distance: 0, coins: 0, boost: 100, speed: 0, altitude: 0 });
  const [notifications, setNotifications] = useState([]);
  
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const animationRef = useRef(null);
  
  // Save whenever stats change
  useEffect(() => {
    if (authChecked) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');

    if (tokenFromUrl) {
        localStorage.setItem('cugs_token', tokenFromUrl);
        console.log('Token saved to localStorage');
        
        // Clean URL BEFORE calling verifyAuth
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Trigger auth re-check
        verifyAuth();
    }
    
    setAuthChecked(true);
  }, [authChecked, verifyAuth]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading && gameState === 'menu') {
        const timer = setTimeout(() => setShowAuthPrompt(true), 3000);
        return () => clearTimeout(timer);
    } else {
        setShowAuthPrompt(false);
    }
    }, [isAuthenticated, isLoading, gameState]);

  useEffect(() => {
    saveGame(stats);
  }, [stats]);
  
  // Show notification
  const notify = useCallback((text, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, text, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 2000);
  }, []);

  const submitScore = async (finalDistance, finalCoins) => {
    if (!isAuthenticated && !localStorage.getItem('cugs_token')) {
        console.log('Not authenticated, skipping score submission');
        return;
    }
    
    try {
        const response = await fetch(`${API_URL}/games/scores/submit`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            ...(localStorage.getItem('cugs_token') && {
            'Authorization': `Bearer ${localStorage.getItem('cugs_token')}`
            })
        },
        credentials: 'include',
        body: JSON.stringify({
            gameId: 'paper-flight',
            score: finalDistance,
            metadata: {
            timestamp: new Date().toISOString(),
            version: '1.0',
            distance: finalDistance,
            coins: finalCoins,
            rings: gameRef.current?.stats?.rings || 0,
            plane: stats.selectedPlane
            }
        })
        });

        if (response.ok) {
        const data = await response.json();
        setScoreSubmitted(true);
        setSubmissionResult(data);
        
        // ⚠️ NO updateUser() call here - prevents infinite loop
        
        if (data.isNewHighScore) {
            console.log('🎉 NEW HIGH SCORE!');
        }
        
        if (data.levelUp) {
            showToast(`🎊 Level Up! Now Level ${data.user.level}!`, 'success');
        }
        
        } else {
        const errorData = await response.json();
        console.error('Score submission failed:', errorData);
        showToast('Failed to save score. Playing offline.', 'error');
        }
    } catch (error) {
        console.error('Failed to submit score:', error);
        showToast('Network error. Score not saved.', 'error');
    }
  };

  
  // Initialize game engine
  const initGame = useCallback(() => {
    sounds.init();
    
    const planeConfig = PLANES[stats.selectedPlane];
    const maxBoost = 100 + stats.boostLevel * 15;
    
    gameRef.current = {
      phase: 'LAUNCH',
      time: 0,
      
      plane: {
        position: { x: 0, y: 8, z: 0 },
        velocity: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        targetRotation: { x: 0, y: 0, z: 0 },
        boost: maxBoost,
        maxBoost,
        flapping: false,
        flapCooldown: 0,
        trail: [],
      },
      
      camera: {
        position: { x: 0, y: 10, z: -15 },
        target: { x: 0, y: 8, z: 0 },
        shake: 0,
      },
      
      input: {
        up: false,
        down: false,
        left: false,
        right: false,
        boost: false,
      },
      
      drag: {
        active: false,
        start: { x: 0, y: 0 },
        current: { x: 0, y: 0 },
        power: 0,
      },
      
      objects: [],
      particles: [],
      thermals: [],
      
      stats: {
        distance: 0,
        maxDistance: 0,
        coins: 0,
        rings: 0,
        combo: 0,
        comboTimer: 0,
      },
      
      milestonesPassed: [],
      currentZone: ZONES[0],
      
      config: {
        planeConfig,
        liftMod: 1 + stats.liftLevel * 0.08,
        dragMod: 1 - stats.aerodynamicsLevel * 0.008,
        boostMod: 1 + stats.boostLevel * 0.1,
        coinRadius: 4 + stats.coinsLevel * 1.5,
      },
    };
    
    // Pre-spawn some objects
    for (let z = 50; z < 300; z += 30) {
      spawnObjectsAt(z);
    }
  }, [stats]);
  
  // Spawn objects at distance
  const spawnObjectsAt = (z) => {
    const game = gameRef.current;
    if (!game) return;
    
    // Coins in patterns
    if (Math.random() < 0.4) {
      const pattern = Math.floor(Math.random() * 4);
      const baseX = (Math.random() - 0.5) * 60;
      const baseY = 5 + Math.random() * 35;
      
      switch (pattern) {
        case 0: // Line
          for (let i = 0; i < 5; i++) {
            game.objects.push({
              type: 'coin',
              position: { x: baseX, y: baseY, z: z + i * 8 },
              active: true,
              rotation: Math.random() * Math.PI * 2,
            });
          }
          break;
        case 1: // Arc
          for (let i = 0; i < 7; i++) {
            const angle = (i / 6) * Math.PI;
            game.objects.push({
              type: 'coin',
              position: { 
                x: baseX + Math.cos(angle) * 15, 
                y: baseY + Math.sin(angle) * 10, 
                z: z + i * 6 
              },
              active: true,
              rotation: Math.random() * Math.PI * 2,
            });
          }
          break;
        case 2: // Cluster
          for (let i = 0; i < 4; i++) {
            game.objects.push({
              type: 'coin',
              position: { 
                x: baseX + (Math.random() - 0.5) * 10, 
                y: baseY + (Math.random() - 0.5) * 10, 
                z: z + (Math.random() - 0.5) * 10 
              },
              active: true,
              rotation: Math.random() * Math.PI * 2,
            });
          }
          break;
        default: // Single
          game.objects.push({
            type: 'coin',
            position: { x: baseX, y: baseY, z },
            active: true,
            rotation: Math.random() * Math.PI * 2,
          });
      }
    }
    
    // Rings
    if (Math.random() < 0.15) {
      game.objects.push({
        type: 'ring',
        position: { 
          x: (Math.random() - 0.5) * 50, 
          y: 10 + Math.random() * 30, 
          z 
        },
        active: true,
        radius: 8 + Math.random() * 4,
      });
    }
    
    // Thermals (updrafts)
    if (Math.random() < 0.1) {
      game.thermals.push({
        position: { x: (Math.random() - 0.5) * 80, y: 0, z },
        radius: 15 + Math.random() * 10,
        strength: 15 + Math.random() * 10,
        active: true,
      });
    }
    
    // Clouds (visual)
    if (Math.random() < 0.2) {
      game.objects.push({
        type: 'cloud',
        position: { 
          x: (Math.random() - 0.5) * 200, 
          y: 40 + Math.random() * 60, 
          z: z + 100 
        },
        active: true,
        scale: 3 + Math.random() * 5,
      });
    }
    
    // Birds (obstacles)
    if (Math.random() < 0.08 && z > 200) {
      game.objects.push({
        type: 'bird',
        position: { 
          x: (Math.random() - 0.5) * 60, 
          y: 15 + Math.random() * 25, 
          z 
        },
        active: true,
        velocity: { x: (Math.random() - 0.5) * 5, y: 0, z: 0 },
        flapPhase: Math.random() * Math.PI * 2,
      });
    }
  };
  
  // Physics update
  const updatePhysics = useCallback((dt) => {
    const game = gameRef.current;
    if (!game || game.phase === 'CRASHED') return;
    
    const { plane, camera, input, config } = game;
    
    if (game.phase === 'LAUNCH') {
      // Slingshot physics
      if (game.drag.active) {
        const dx = game.drag.current.x - game.drag.start.x;
        const dy = game.drag.current.y - game.drag.start.y;
        const pullDist = Math.sqrt(dx * dx + dy * dy);
        const maxPull = 200;
        game.drag.power = Math.min(pullDist / maxPull, 1);
        
        // Position plane based on drag
        const pullScale = game.drag.power * 8;
        plane.position = {
          x: -dx * 0.02 * pullScale,
          y: 8 - Math.abs(dy) * 0.01 * pullScale,
          z: -Math.abs(dy) * 0.03 * pullScale,
        };
        
        // Angle towards launch direction
        plane.rotation.y = Math.atan2(-dx, Math.abs(dy)) * 0.3;
        plane.rotation.x = -0.2 - game.drag.power * 0.3;
        plane.rotation.z = dx * 0.002;
      } else {
        // Idle bobbing
        plane.position = { x: 0, y: 8 + Math.sin(game.time * 2) * 0.2, z: 0 };
        plane.rotation = { x: 0, y: 0, z: Math.sin(game.time * 3) * 0.05 };
      }
      
      // Camera for launch
      camera.position = vec3.lerp(camera.position, { x: 0, y: 12, z: -18 }, dt * 2);
      camera.target = vec3.lerp(camera.target, plane.position, dt * 5);
      
    } else if (game.phase === 'FLIGHT') {
      // === INPUT HANDLING ===
      const pitchRate = 2.5;
      const rollRate = 3.5;
      const maxPitch = 1.2;
      const maxRoll = 1.5;
      
      if (input.up) plane.targetRotation.x = maxPitch;
      else if (input.down) plane.targetRotation.x = -maxPitch;
      else plane.targetRotation.x = 0;
      
      if (input.left) plane.targetRotation.z = maxRoll;
      else if (input.right) plane.targetRotation.z = -maxRoll;
      else plane.targetRotation.z = 0;
      
      // Smooth rotation
      plane.rotation.x = lerp(plane.rotation.x, plane.targetRotation.x, pitchRate * dt);
      plane.rotation.z = lerp(plane.rotation.z, plane.targetRotation.z, rollRate * dt);
      
      // Auto-center roll slowly
      if (!input.left && !input.right) {
        plane.rotation.z *= 0.96;
      }
      
      // Yaw from roll (banking turns)
      plane.rotation.y += plane.rotation.z * dt * 0.8;
      
      // === PHYSICS ===
      const speed = vec3.length(plane.velocity);
      const forward = rotate3D({ x: 0, y: 0, z: 1 }, plane.rotation);
      const up = rotate3D({ x: 0, y: 1, z: 0 }, plane.rotation);
      
      // Gravity
      const gravity = { x: 0, y: -PHYSICS.GRAVITY, z: 0 };
      plane.velocity = vec3.add(plane.velocity, vec3.scale(gravity, dt));
      
      // Lift (perpendicular to velocity, scales with speed²)
      const liftCoeff = PHYSICS.BASE_LIFT * config.liftMod * config.planeConfig.lift;
      const liftMag = speed * speed * liftCoeff * (1 - Math.abs(plane.rotation.z) * 0.3);
      
      // Angle of attack affects lift
      const aoa = plane.rotation.x;
      const aoaLiftMod = Math.cos(aoa) * (1 + Math.sin(aoa) * 0.5);
      
      const lift = vec3.scale(up, liftMag * aoaLiftMod);
      plane.velocity = vec3.add(plane.velocity, vec3.scale(lift, dt));
      
      // Drag
      const dragCoeff = PHYSICS.BASE_DRAG * config.dragMod / config.planeConfig.drag;
      plane.velocity = vec3.scale(plane.velocity, Math.pow(dragCoeff, dt * 60));
      
      // Stall behavior
      if (speed < PHYSICS.STALL_SPEED) {
        // Nose drops when stalling
        plane.rotation.x = lerp(plane.rotation.x, -0.5, dt * 2);
        // Add wobble
        plane.rotation.z += Math.sin(game.time * 15) * dt * 0.5;
      }
      
      // Boost
      if (input.boost && plane.boost > 0) {
        const boostPower = PHYSICS.BOOST_FORCE * config.boostMod * config.planeConfig.boost;
        plane.velocity = vec3.add(plane.velocity, vec3.scale(forward, boostPower * dt));
        plane.boost -= 30 * dt;
        plane.boost = Math.max(0, plane.boost);
        camera.shake = Math.min(camera.shake + dt * 0.5, 0.15); // Much subtler shake
        
        // Boost particles - small and subtle
        if (Math.random() > 0.7) {
          game.particles.push({
            position: vec3.sub(plane.position, vec3.scale(forward, 2.5)),
            velocity: vec3.add(
              vec3.scale(forward, -15),
              { x: (Math.random() - 0.5) * 2, y: (Math.random() - 0.5) * 2, z: (Math.random() - 0.5) * 2 }
            ),
            life: 0.2,
            maxLife: 0.2,
            color: '#60a5fa',
            size: 0.08 + Math.random() * 0.06,
          });
        }
      } else {
        // Regenerate boost
        plane.boost = Math.min(plane.boost + 8 * dt, plane.maxBoost);
      }
      
      // Thermals
      game.thermals.forEach(thermal => {
        if (!thermal.active) return;
        const dist2D = Math.sqrt(
          Math.pow(plane.position.x - thermal.position.x, 2) +
          Math.pow(plane.position.z - thermal.position.z, 2)
        );
        if (dist2D < thermal.radius && plane.position.y < 60) {
          const strength = (1 - dist2D / thermal.radius) * thermal.strength;
          plane.velocity.y += strength * dt;
          
          // Thermal particles
          if (Math.random() > 0.8) {
            game.particles.push({
              position: {
                x: thermal.position.x + (Math.random() - 0.5) * thermal.radius,
                y: Math.random() * 30,
                z: thermal.position.z + (Math.random() - 0.5) * thermal.radius,
              },
              velocity: { x: 0, y: 8, z: 0 },
              life: 1.5,
              maxLife: 1.5,
              color: 'rgba(255,255,255,0.3)',
              size: 1,
            });
          }
        }
      });
      
      // Apply velocity
      plane.position = vec3.add(plane.position, vec3.scale(plane.velocity, dt));
      
      // Trail
      if (game.time % 0.05 < dt) {
        plane.trail.push({ ...plane.position, age: 0 });
        if (plane.trail.length > 50) plane.trail.shift();
      }
      plane.trail.forEach(t => t.age += dt);
      
      // Update distance
      game.stats.distance = Math.max(game.stats.distance, plane.position.z);
      game.stats.maxDistance = Math.max(game.stats.maxDistance, plane.position.z);
      
      // Combo timer
      if (game.stats.comboTimer > 0) {
        game.stats.comboTimer -= dt;
        if (game.stats.comboTimer <= 0) {
          game.stats.combo = 0;
        }
      }
      
      // Check milestones
      MILESTONES.forEach(m => {
        if (plane.position.z >= m && !game.milestonesPassed.includes(m)) {
          game.milestonesPassed.push(m);
          notify(`🎯 ${m}m!`, 'milestone');
          sounds.play('milestone');
          camera.shake = 0.3;
        }
      });
      
      // Update zone
      const newZone = [...ZONES].reverse().find(z => plane.position.z >= z.start);
      if (newZone && newZone !== game.currentZone) {
        game.currentZone = newZone;
        notify(`📍 ${newZone.name}`, 'zone');
      }
      
      // === COLLISIONS ===
      const coinRadius = config.coinRadius;
      
      game.objects.forEach(obj => {
        if (!obj.active) return;
        const dist = vec3.length(vec3.sub(obj.position, plane.position));
        
        if (obj.type === 'coin' && dist < coinRadius) {
          obj.active = false;
          game.stats.coins++;
          game.stats.combo++;
          game.stats.comboTimer = 2;
          sounds.play('coin');
          
          // Sparkle particles
          for (let i = 0; i < 8; i++) {
            game.particles.push({
              position: { ...obj.position },
              velocity: {
                x: (Math.random() - 0.5) * 15,
                y: (Math.random() - 0.5) * 15,
                z: (Math.random() - 0.5) * 15,
              },
              life: 0.5,
              maxLife: 0.5,
              color: '#fbbf24',
              size: 0.4,
            });
          }
        }
        
        if (obj.type === 'ring' && dist < obj.radius + 2) {
          obj.active = false;
          game.stats.rings++;
          game.stats.combo += 3;
          game.stats.comboTimer = 3;
          sounds.play('ring');
          
          // Speed boost
          const boostDir = vec3.normalize(plane.velocity);
          plane.velocity = vec3.add(plane.velocity, vec3.scale(boostDir, 25));
          camera.shake = 0.4;
          notify('🔥 Ring Boost!', 'powerup');
          
          // Ring particles
          for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2;
            game.particles.push({
              position: { ...obj.position },
              velocity: {
                x: Math.cos(angle) * 20,
                y: Math.sin(angle) * 20,
                z: 0,
              },
              life: 0.6,
              maxLife: 0.6,
              color: '#4ade80',
              size: 0.5,
            });
          }
        }
        
        if (obj.type === 'bird' && dist < 3) {
          // Turbulence from bird
          plane.velocity = vec3.add(plane.velocity, {
            x: (Math.random() - 0.5) * 10,
            y: -5,
            z: -5,
          });
          plane.rotation.z += (Math.random() - 0.5) * 0.5;
          camera.shake = 0.5;
          obj.active = false;
          notify('🐦 Bird strike!', 'warning');
        }
      });
      
      // Ground collision
      if (plane.position.y <= PHYSICS.GROUND_LEVEL) {
        game.phase = 'CRASHED';
        plane.position.y = PHYSICS.GROUND_LEVEL;
        camera.shake = 0.8;
        sounds.play('crash');
        
        // Crash particles
        for (let i = 0; i < 30; i++) {
          game.particles.push({
            position: { ...plane.position },
            velocity: {
              x: (Math.random() - 0.5) * 20,
              y: Math.random() * 15,
              z: (Math.random() - 0.5) * 20,
            },
            life: 1,
            maxLife: 1,
            color: '#f8fafc',
            size: 0.3 + Math.random() * 0.5,
          });
        }
        
        // Trigger game over
        setTimeout(() => {
        const finalDistance = Math.floor(game.stats.maxDistance);
        const finalCoins = game.stats.coins * (1 + Math.floor(game.stats.rings * 0.5));
        const isNewBest = finalDistance > stats.bestDistance;
        
        setLastRun({
            distance: finalDistance,
            coins: finalCoins,
            rings: game.stats.rings,
            newBest: isNewBest,
        });
        
        setStats(prev => ({
            ...prev,
            totalCoins: prev.totalCoins + finalCoins,
            bestDistance: Math.max(prev.bestDistance, finalDistance),
            totalFlights: prev.totalFlights + 1,
        }));
        
        // ADD THIS LINE:
        submitScore(finalDistance, finalCoins);
        
        setGameState('gameOver');
        }, 1500);
      }
      
      // === CAMERA ===
      const camDist = 12 + Math.min(speed * 0.05, 8);
      const camHeight = 3 + plane.position.y * 0.1;
      
      const targetCam = {
        x: plane.position.x * 0.3,
        y: plane.position.y + camHeight,
        z: plane.position.z - camDist,
      };
      
      camera.position = vec3.lerp(camera.position, targetCam, dt * 4);
      camera.target = vec3.lerp(camera.target, plane.position, dt * 8);
      camera.shake *= 0.92;
      
      // Spawn new objects
      const spawnZ = plane.position.z + 300;
      if (game.objects.filter(o => o.position.z > spawnZ - 50).length < 5) {
        spawnObjectsAt(spawnZ);
      }
      
      // Cleanup old objects
      game.objects = game.objects.filter(o => o.active && o.position.z > plane.position.z - 50);
      game.thermals = game.thermals.filter(t => t.position.z > plane.position.z - 100);
      
      // Update bird positions
      game.objects.forEach(obj => {
        if (obj.type === 'bird' && obj.active) {
          obj.position.x += obj.velocity.x * dt;
          obj.flapPhase += dt * 10;
        }
        if (obj.type === 'coin' && obj.active) {
          obj.rotation += dt * 3;
        }
      });
      
      // Update particles
      game.particles = game.particles.filter(p => {
        p.life -= dt;
        p.position = vec3.add(p.position, vec3.scale(p.velocity, dt));
        p.velocity.y -= 5 * dt; // Gravity on particles
        return p.life > 0;
      });
      
      // Update HUD
      setHudData({
        distance: Math.floor(plane.position.z),
        coins: game.stats.coins,
        boost: Math.floor((plane.boost / plane.maxBoost) * 100),
        speed: Math.floor(speed),
        altitude: Math.floor(plane.position.y),
        combo: game.stats.combo,
      });
    }
    
    game.time += dt;
  }, [stats, notify]);
  
  // Render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const game = gameRef.current;
    if (!canvas || !game) return;
    
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    
    const { plane, camera, currentZone } = game;
    
    // Apply shake
    ctx.save();
    if (camera.shake > 0.01) {
      ctx.translate(
        (Math.random() - 0.5) * camera.shake * 40,
        (Math.random() - 0.5) * camera.shake * 40
      );
    }
    
    // === SKY ===
    const skyGrad = ctx.createLinearGradient(0, 0, 0, H);
    skyGrad.addColorStop(0, currentZone.skyTop);
    skyGrad.addColorStop(0.6, currentZone.skyBottom);
    skyGrad.addColorStop(1, '#e0f2fe');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, W, H);
    
    const horizonY = H * 0.75;
    const horizonGlow = ctx.createRadialGradient(W * 0.5, horizonY, 0, W * 0.5, horizonY, W * 0.6);
    horizonGlow.addColorStop(0, 'rgba(255, 250, 230, 0.3)');
    horizonGlow.addColorStop(0.5, 'rgba(255, 220, 180, 0.1)');
    horizonGlow.addColorStop(1, 'rgba(255, 220, 180, 0)');
    ctx.fillStyle = horizonGlow;
    ctx.fillRect(0, horizonY - H * 0.3, W, H * 0.6);

    // Sun with glow
    const sunX = W * 0.8;
    const sunY = H * 0.12;

    // Sun glow layers
    const sunGlow = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 120);
    sunGlow.addColorStop(0, 'rgba(255, 250, 220, 0.8)');
    sunGlow.addColorStop(0.3, 'rgba(255, 240, 200, 0.3)');
    sunGlow.addColorStop(0.6, 'rgba(255, 220, 150, 0.1)');
    sunGlow.addColorStop(1, 'rgba(255, 220, 150, 0)');
    ctx.fillStyle = sunGlow;
    ctx.beginPath();
    ctx.arc(sunX, sunY, 120, 0, Math.PI * 2);
    ctx.fill();

    // Sun core
    ctx.fillStyle = 'rgba(255, 252, 240, 0.95)';
    ctx.beginPath();
    ctx.arc(sunX, sunY, 25, 0, Math.PI * 2);
    ctx.fill();

    // Distant background clouds (static, decorative)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    const cloudPositions = [
    { x: W * 0.1, y: H * 0.2, s: 60 },
    { x: W * 0.3, y: H * 0.15, s: 80 },
    { x: W * 0.6, y: H * 0.25, s: 50 },
    { x: W * 0.15, y: H * 0.4, s: 70 },
    { x: W * 0.85, y: H * 0.3, s: 55 },
    ];
    cloudPositions.forEach(c => {
    ctx.beginPath();
    ctx.arc(c.x, c.y, c.s, 0, Math.PI * 2);
    ctx.arc(c.x + c.s * 0.7, c.y + c.s * 0.1, c.s * 0.7, 0, Math.PI * 2);
    ctx.arc(c.x - c.s * 0.5, c.y + c.s * 0.15, c.s * 0.5, 0, Math.PI * 2);
    ctx.arc(c.x + c.s * 0.3, c.y - c.s * 0.3, c.s * 0.4, 0, Math.PI * 2);
    ctx.fill();
    });

    // Subtle sun rays
    ctx.save();
    ctx.globalAlpha = 0.03;
    ctx.fillStyle = '#fff';
    for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2 + game.time * 0.02;
    ctx.beginPath();
    ctx.moveTo(sunX, sunY);
    ctx.lineTo(sunX + Math.cos(angle) * 400, sunY + Math.sin(angle) * 400);
    ctx.lineTo(sunX + Math.cos(angle + 0.1) * 400, sunY + Math.sin(angle + 0.1) * 400);
    ctx.closePath();
    ctx.fill();
    }
    ctx.restore();
        
    // === 3D PROJECTION ===
    const project = (pos) => {
      const rel = vec3.sub(pos, camera.position);
      
      // Look direction
      const lookDir = vec3.normalize(vec3.sub(camera.target, camera.position));
      const right = vec3.normalize({ x: lookDir.z, y: 0, z: -lookDir.x });
      const up = { x: 0, y: 1, z: 0 };
      
      const fov = 800;
      const z = vec3.dot(rel, lookDir);
      if (z < 1) return null;
      
      const x = vec3.dot(rel, right);
      const y = vec3.dot(rel, up);
      
      const scale = fov / z;
      return {
        x: W / 2 + x * scale,
        y: H / 2 - y * scale,
        scale,
        z,
      };
    };
    
    // === GROUND GRID ===
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 1;
    
    const gridSize = 50;
    const gridRange = 500;
    const camZ = camera.position.z;

    // Ground plane with gradient
    const groundY = H * 0.85;
    const groundGrad = ctx.createLinearGradient(0, groundY - 50, 0, H);
    groundGrad.addColorStop(0, 'rgba(34, 197, 94, 0.1)');
    groundGrad.addColorStop(0.3, 'rgba(34, 197, 94, 0.3)');
    groundGrad.addColorStop(1, 'rgba(22, 163, 74, 0.5)');
    ctx.fillStyle = groundGrad;
    ctx.fillRect(0, groundY - 50, W, H - groundY + 50);

    // Grid lines with fade
    ctx.beginPath();
    for (let x = -gridRange; x <= gridRange; x += gridSize) {
    const p1 = project({ x, y: 0, z: camZ + 10 });
    const p2 = project({ x, y: 0, z: camZ + 600 });
    if (p1 && p2) {
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
    }
    }
    const startZ = Math.floor(camZ / gridSize) * gridSize;
    for (let z = startZ; z < camZ + 600; z += gridSize) {
    const p1 = project({ x: -gridRange, y: 0, z });
    const p2 = project({ x: gridRange, y: 0, z });
    if (p1 && p2) {
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
    }
    }
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Horizon line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, groundY);
    ctx.lineTo(W, groundY);
    ctx.stroke();
    
    // === COLLECT RENDER ITEMS ===
    const renderItems = [];
    
    // Thermals (subtle visual)
    game.thermals.forEach(thermal => {
      const p = project(thermal.position);
      if (p) {
        renderItems.push({
          z: thermal.position.z,
          draw: () => {
            const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, thermal.radius * p.scale * 2);
            gradient.addColorStop(0, 'rgba(255,255,255,0.15)');
            gradient.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = gradient;
            ctx.fillRect(p.x - 100, p.y - 100, 200, 200);
          },
        });
      }
    });
    
    // Objects
    game.objects.forEach(obj => {
      if (!obj.active) return;
      const p = project(obj.position);
      if (!p || p.z < 1) return;
      
      renderItems.push({
        z: obj.position.z,
        draw: () => {
          const size = (obj.scale || 1) * p.scale;
          
            if (obj.type === 'coin') {
            // Smaller, cleaner coins
            const coinSize = Math.min(size * 6, 8); // Capped size
            const wobble = Math.sin(obj.rotation) * 0.3;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.scale(1, 0.6 + wobble * 0.2);
            
            ctx.fillStyle = '#fbbf24';
            ctx.strokeStyle = '#b45309';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(0, 0, coinSize, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            
            // Small shine
            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.beginPath();
            ctx.arc(-coinSize * 0.25, -coinSize * 0.25, coinSize * 0.3, 0, Math.PI * 2);
            ctx.fill();
            
            ctx.restore();
            }
          
          if (obj.type === 'ring') {
            ctx.strokeStyle = '#4ade80';
            ctx.lineWidth = 6 * size;
            ctx.shadowColor = '#4ade80';
            ctx.shadowBlur = 20;
            ctx.beginPath();
            ctx.arc(p.x, p.y, obj.radius * size * 3, 0, Math.PI * 2);
            ctx.stroke();
            ctx.shadowBlur = 0;
          }
          
          if (obj.type === 'cloud') {
            ctx.fillStyle = 'rgba(255,255,255,0.5)';
            const cs = size * 15;
            ctx.beginPath();
            ctx.arc(p.x, p.y, cs, 0, Math.PI * 2);
            ctx.arc(p.x + cs, p.y, cs * 0.8, 0, Math.PI * 2);
            ctx.arc(p.x - cs, p.y, cs * 0.7, 0, Math.PI * 2);
            ctx.arc(p.x + cs * 0.5, p.y - cs * 0.5, cs * 0.6, 0, Math.PI * 2);
            ctx.fill();
          }
          
          if (obj.type === 'bird') {
            const wingOffset = Math.sin(obj.flapPhase) * 0.3;
            ctx.fillStyle = '#1e293b';
            ctx.beginPath();
            // Body
            ctx.ellipse(p.x, p.y, 10 * size, 5 * size, 0, 0, Math.PI * 2);
            ctx.fill();
            // Wings
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p.x - 20 * size, p.y - 15 * size * wingOffset);
            ctx.lineTo(p.x - 5 * size, p.y);
            ctx.fill();
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p.x + 20 * size, p.y - 15 * size * wingOffset);
            ctx.lineTo(p.x + 5 * size, p.y);
            ctx.fill();
          }
        },
      });
    });
    
    // Particles - rendered small to not block view
    game.particles.forEach(particle => {
      const p = project(particle.position);
      if (!p) return;
      
      renderItems.push({
        z: particle.position.z,
        draw: () => {
          ctx.globalAlpha = (particle.life / particle.maxLife) * 0.7;
          ctx.fillStyle = particle.color;
          ctx.beginPath();
          // Capped size to prevent blocking screen
          const size = Math.min(particle.size * 4 * p.scale, 6);
          ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        },
      });
    });
    
    // Trail
    if (plane.trail.length > 1) {
      renderItems.push({
        z: plane.position.z - 5,
        draw: () => {
          ctx.strokeStyle = 'rgba(96, 165, 250, 0.5)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          plane.trail.forEach((t, i) => {
            const p = project(t);
            if (p) {
              ctx.globalAlpha = 1 - t.age;
              if (i === 0) ctx.moveTo(p.x, p.y);
              else ctx.lineTo(p.x, p.y);
            }
          });
          ctx.stroke();
          ctx.globalAlpha = 1;
        },
      });
    }
    
    // Slingshot (launch phase)
    if (game.phase === 'LAUNCH') {
      const leftPost = project({ x: -5, y: 8, z: 0 });
      const rightPost = project({ x: 5, y: 8, z: 0 });
      const leftBase = project({ x: -5, y: 0, z: 0 });
      const rightBase = project({ x: 5, y: 0, z: 0 });
      const planePos = project(plane.position);
      
      if (leftPost && rightPost && leftBase && rightBase && planePos) {
        // Posts
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 8;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(leftBase.x, leftBase.y);
        ctx.lineTo(leftPost.x, leftPost.y);
        ctx.moveTo(rightBase.x, rightBase.y);
        ctx.lineTo(rightPost.x, rightPost.y);
        ctx.stroke();
        
        // Rubber band
        const tension = game.drag.power;
        ctx.strokeStyle = `rgb(${239 - tension * 50}, ${68 + tension * 50}, ${68})`;
        ctx.lineWidth = 4 + tension * 3;
        ctx.beginPath();
        ctx.moveTo(leftPost.x, leftPost.y);
        ctx.lineTo(planePos.x, planePos.y);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(rightPost.x, rightPost.y);
        ctx.lineTo(planePos.x, planePos.y);
        ctx.stroke();
        
        // Power meter
        if (game.drag.active) {
          const meterWidth = 150;
          const meterHeight = 20;
          const meterX = W / 2 - meterWidth / 2;
          const meterY = H - 80;
          
          ctx.fillStyle = 'rgba(0,0,0,0.5)';
          ctx.fillRect(meterX - 2, meterY - 2, meterWidth + 4, meterHeight + 4);
          
          const gradient = ctx.createLinearGradient(meterX, 0, meterX + meterWidth, 0);
          gradient.addColorStop(0, '#22c55e');
          gradient.addColorStop(0.5, '#fbbf24');
          gradient.addColorStop(1, '#ef4444');
          ctx.fillStyle = gradient;
          ctx.fillRect(meterX, meterY, meterWidth * tension, meterHeight);
          
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.strokeRect(meterX, meterY, meterWidth, meterHeight);
          
          ctx.fillStyle = '#fff';
          ctx.font = 'bold 14px system-ui';
          ctx.textAlign = 'center';
          ctx.fillText('POWER', W / 2, meterY - 8);
        }
      }
    }
    
    // Paper Plane
    renderItems.push({
      z: plane.position.z,
      draw: () => {
        const planeColor = PLANES[stats.selectedPlane].color;
        
        // Transform vertices
        const transformed = PLANE_MODEL.vertices.map(v => {
          const rotated = rotate3D(v, plane.rotation);
          return vec3.add(rotated, plane.position);
        });
        
        // Project and sort faces by depth
        const projectedVerts = transformed.map(v => project(v));
        
        // Calculate face depths for sorting
        const faceData = PLANE_MODEL.faces.map(face => {
          const center = face.indices.reduce((acc, i) => {
            return vec3.add(acc, vec3.scale(transformed[i], 1/3));
          }, { x: 0, y: 0, z: 0 });
          return { ...face, depth: center.z };
        }).sort((a, b) => b.depth - a.depth);
        
        faceData.forEach(face => {
          const p0 = projectedVerts[face.indices[0]];
          const p1 = projectedVerts[face.indices[1]];
          const p2 = projectedVerts[face.indices[2]];
          
          if (p0 && p1 && p2) {
            // Shade color based on face normal
            const shade = face.shade;
            const r = parseInt(planeColor.slice(1, 3), 16);
            const g = parseInt(planeColor.slice(3, 5), 16);
            const b = parseInt(planeColor.slice(5, 7), 16);
            
            ctx.fillStyle = `rgb(${Math.floor(r * shade)}, ${Math.floor(g * shade)}, ${Math.floor(b * shade)})`;
            ctx.strokeStyle = '#64748b';
            ctx.lineWidth = 1;
            
            ctx.beginPath();
            ctx.moveTo(p0.x, p0.y);
            ctx.lineTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
          }
        });
        
        // Engine glow when boosting - small and subtle
        if (game.input.boost && plane.boost > 0) {
          const tail = projectedVerts[1];
          if (tail && tail.scale < 2) { // Only render when not too close
            ctx.shadowColor = '#3b82f6';
            ctx.shadowBlur = 8;
            ctx.fillStyle = 'rgba(96, 165, 250, 0.6)';
            ctx.beginPath();
            // Much smaller glow, capped size
            const glowSize = Math.min(3 * tail.scale, 5);
            ctx.arc(tail.x, tail.y, glowSize, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
          }
        }
      },
    });
    
    // Sort and render all items (far to near)
    renderItems.sort((a, b) => b.z - a.z);
    renderItems.forEach(item => item.draw());
    
    ctx.restore();
  }, [stats.selectedPlane]);
  
  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    initGame();
    
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    
    let lastTime = performance.now();
    
    const loop = (time) => {
      const dt = Math.min((time - lastTime) / 1000, 0.05);
      lastTime = time;
      
      updatePhysics(dt);
      render();
      
      animationRef.current = requestAnimationFrame(loop);
    };
    
    animationRef.current = requestAnimationFrame(loop);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameState, initGame, updatePhysics, render]);
  
  // Input handlers
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const handleKeyDown = (e) => {
      const game = gameRef.current;
      if (!game) return;
      
      switch (e.code) {
        case 'ArrowUp':
        case 'KeyW':
          game.input.up = true;
          break;
        case 'ArrowDown':
        case 'KeyS':
          game.input.down = true;
          break;
        case 'ArrowLeft':
        case 'KeyA':
          game.input.left = true;
          break;
        case 'ArrowRight':
        case 'KeyD':
          game.input.right = true;
          break;
        case 'Space':
          game.input.boost = true;
          if (game.phase === 'FLIGHT' && game.plane.boost > 0) {
            sounds.play('boost');
          }
          break;
      }
    };
    
    const handleKeyUp = (e) => {
      const game = gameRef.current;
      if (!game) return;
      
      switch (e.code) {
        case 'ArrowUp':
        case 'KeyW':
          game.input.up = false;
          break;
        case 'ArrowDown':
        case 'KeyS':
          game.input.down = false;
          break;
        case 'ArrowLeft':
        case 'KeyA':
          game.input.left = false;
          break;
        case 'ArrowRight':
        case 'KeyD':
          game.input.right = false;
          break;
        case 'Space':
          game.input.boost = false;
          break;
      }
    };
    
    const handleMouseDown = (e) => {
      const game = gameRef.current;
      if (!game || game.phase !== 'LAUNCH') return;
      
      game.drag.active = true;
      game.drag.start = { x: e.clientX, y: e.clientY };
      game.drag.current = { x: e.clientX, y: e.clientY };
    };
    
    const handleMouseMove = (e) => {
      const game = gameRef.current;
      if (!game || !game.drag.active) return;
      
      game.drag.current = { x: e.clientX, y: e.clientY };
    };
    
    const handleMouseUp = () => {
      const game = gameRef.current;
      if (!game || !game.drag.active) return;
      
      game.drag.active = false;
      
      if (game.drag.power > 0.1 && game.phase === 'LAUNCH') {
        // Launch!
        const launchPower = 30 + game.drag.power * 50;
        const dx = game.drag.current.x - game.drag.start.x;
        const dy = game.drag.current.y - game.drag.start.y;
        
        const angle = Math.atan2(-dx, Math.abs(dy));
        
        game.plane.velocity = {
          x: -Math.sin(angle) * launchPower * 0.3,
          y: 5 + game.drag.power * 15,
          z: launchPower,
        };
        
        game.plane.rotation = {
          x: -0.3,
          y: angle * 0.3,
          z: 0,
        };
        
        game.phase = 'FLIGHT';
        sounds.play('launch');
      }
      
      game.drag.power = 0;
    };
    
    const handleTouchStart = (e) => {
      e.preventDefault();
      handleMouseDown({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
    };
    
    const handleTouchMove = (e) => {
      e.preventDefault();
      handleMouseMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
    };
    
    const handleTouchEnd = (e) => {
      e.preventDefault();
      handleMouseUp();
    };
    
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', handleResize);
    };
  }, [gameState]);
  
  // ============================================
  // UI COMPONENTS
  // ============================================
  
  const MainMenu = () => (
    <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom, #38bdf8, #bae6fd)',
        overflow: 'hidden'
    }}>
        {!isLoading && (
      <>
        {isAuthenticated && user && (
          <div style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(12px)',
            padding: '0.75rem 1rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '0.5rem',
                height: '0.5rem',
                backgroundColor: '#22c55e',
                borderRadius: '9999px',
                animation: 'pulse 2s infinite'
              }} />
              <span style={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>
                {user.username}
              </span>
              <span style={{ 
                color: '#fbbf24', 
                fontSize: '0.75rem', 
                fontWeight: 700,
                marginLeft: '0.25rem'
              }}>
                LVL {user.level}
              </span>
            </div>
          </div>
        )}

        {!isAuthenticated && showAuthPrompt && (
          <div style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'linear-gradient(to right, #8b5cf6, #ec4899)',
            padding: '1rem',
            borderRadius: '0.75rem',
            maxWidth: '20rem',
            zIndex: 10,
            border: '2px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
          }}>
            <button
              onClick={() => setShowAuthPrompt(false)}
              style={{
                position: 'absolute',
                top: '0.5rem',
                right: '0.5rem',
                background: 'transparent',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                fontSize: '1.25rem',
                padding: 0,
                width: '1.5rem',
                height: '1.5rem'
              }}
            >
              ×
            </button>
            <div style={{ paddingRight: '1rem' }}>
              <p style={{ 
                fontWeight: 700, 
                color: 'white', 
                fontSize: '0.875rem',
                marginBottom: '0.5rem'
              }}>
                💾 Save Your Flights!
              </p>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                fontSize: '0.75rem',
                marginBottom: '0.75rem',
                lineHeight: 1.5
              }}>
                Login to track progress, compete globally, and earn rewards!
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <a
                  href={`${config.authBaseUrl}/login`}
                  style={{
                    flex: 1,
                    backgroundColor: 'white',
                    color: 'black',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    textAlign: 'center',
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: '0.75rem'
                  }}
                >
                  Login
                </a>
                <button
                  onClick={() => setShowAuthPrompt(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '0.75rem',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )}
        {/* Animated clouds background */}
        <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
        }}>
        {[...Array(6)].map((_, i) => (
            <div
            key={i}
            style={{
                position: 'absolute',
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                borderRadius: '9999px',
                filter: 'blur(24px)',
                width: 100 + Math.random() * 150,
                height: 50 + Math.random() * 50,
                left: `${(i * 20) % 100}%`,
                top: `${20 + Math.random() * 60}%`,
                animation: `float ${10 + i * 2}s ease-in-out infinite`,
                animationDelay: `${i * -2}s`,
            }}
            />
        ))}
        </div>
        
        <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
        <h1 style={{
            fontSize: '4.5rem',
            fontWeight: 900,
            color: 'white',
            marginBottom: '0.5rem',
            textShadow: '4px 4px 0 #0284c7, 8px 8px 0 rgba(0,0,0,0.1), 0 4px 20px rgba(0,0,0,0.3)'
        }}>
            PAPER FLIGHT
        </h1>
        <p style={{
            color: '#0369a1',
            fontSize: '1.25rem',
            fontWeight: 500,
            marginBottom: '2rem'
        }}>
            Launch · Fly · Explore
        </p>
        
        <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(12px)',
            borderRadius: '1rem',
            padding: '1.5rem',
            marginBottom: '2rem',
            border: '1px solid rgba(255, 255, 255, 0.3)'
        }}>
            <div style={{
            display: 'flex',
            gap: '2rem',
            justifyContent: 'center',
            color: 'white'
            }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.875rem', fontWeight: 700 }}>
                {Math.floor(stats.bestDistance)}m
                </div>
                <div style={{ fontSize: '0.875rem', opacity: 0.75 }}>Best Distance</div>
            </div>
            <div style={{ height: '3rem', width: '1px', backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.875rem', fontWeight: 700, color: '#fde047' }}>
                {stats.totalCoins}
                </div>
                <div style={{ fontSize: '0.875rem', opacity: 0.75 }}>Total Coins</div>
            </div>
            <div style={{ height: '3rem', width: '1px', backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.875rem', fontWeight: 700 }}>{stats.totalFlights}</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.75 }}>Flights</div>
            </div>
            </div>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
            onClick={() => setGameState('playing')}
            style={{
                padding: '1rem 2.5rem',
                background: 'linear-gradient(to bottom, #4ade80, #16a34a)',
                borderRadius: '0.75rem',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: 'white',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                border: 'none',
                borderBottom: '4px solid #15803d',
                cursor: 'pointer',
                transition: 'all 0.2s',
                transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.background = 'linear-gradient(to bottom, #86efac, #22c55e)';
            }}
            onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.background = 'linear-gradient(to bottom, #4ade80, #16a34a)';
            }}
            onMouseDown={(e) => e.target.style.transform = 'scale(0.95)'}
            onMouseUp={(e) => e.target.style.transform = 'scale(1.05)'}
            >
            ✈️ FLY NOW
            </button>
            
            {/* <button
            onClick={() => setGameState('shop')}
            style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(to bottom, #c084fc, #9333ea)',
                borderRadius: '0.75rem',
                fontWeight: 700,
                fontSize: '1.25rem',
                color: 'white',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                border: 'none',
                borderBottom: '4px solid #7e22ce',
                cursor: 'pointer',
                transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.background = 'linear-gradient(to bottom, #d8b4fe, #a855f7)';
            }}
            onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.background = 'linear-gradient(to bottom, #c084fc, #9333ea)';
            }}
            >
            🛒 SHOP
            </button> */}
        </div>
        
        <p style={{
            marginTop: '2rem',
            color: 'rgba(7, 89, 133, 0.7)',
            fontSize: '0.875rem'
        }}>
            Drag back to launch • Arrow keys to steer • Space to boost
        </p>
        </div>
        
        <style>{`
        @keyframes float {
            0%, 100% { transform: translateX(-10%) translateY(0); }
            50% { transform: translateX(10%) translateY(-20px); }
        }
        `}</style>
    </div>
    );

    const Shop = () => {
    const getUpgradeCost = (level) => Math.floor(50 * Math.pow(1.5, level));
    
    const buyUpgrade = (key) => {
        const level = stats[`${key}Level`];
        const maxLevel = UPGRADES[key].maxLevel;
        if (level >= maxLevel) return;
        
        const cost = getUpgradeCost(level);
        if (stats.totalCoins >= cost) {
        setStats(prev => ({
            ...prev,
            totalCoins: prev.totalCoins - cost,
            [`${key}Level`]: prev[`${key}Level`] + 1,
        }));
        sounds.play('coin');
        }
    };
    
    const buyPlane = (id) => {
        const plane = PLANES[id];
        if (stats.unlockedPlanes.includes(id)) {
        setStats(prev => ({ ...prev, selectedPlane: id }));
        } else if (stats.totalCoins >= plane.cost) {
        setStats(prev => ({
            ...prev,
            totalCoins: prev.totalCoins - plane.cost,
            unlockedPlanes: [...prev.unlockedPlanes, id],
            selectedPlane: id,
        }));
        sounds.play('milestone');
        }
    };
    
    return (
        <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to bottom, #1e293b, #0f172a)',
        overflowY: 'auto',
        padding: '2rem'
        }}>
        <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
            <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '2rem'
            }}>
            <button
                onClick={() => setGameState('menu')}
                style={{
                padding: '0.75rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '9999px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1.25rem',
                transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
                ← Back
            </button>
            <h2 style={{ fontSize: '1.875rem', fontWeight: 700, color: 'white' }}>
                Hangar Shop
            </h2>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'rgba(234, 179, 8, 0.2)',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                border: '1px solid rgba(234, 179, 8, 0.3)'
            }}>
                <span style={{ fontSize: '1.5rem' }}>🪙</span>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fef3c7' }}>
                {stats.totalCoins}
                </span>
            </div>
            </div>
            
            {/* Upgrades */}
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'white', marginBottom: '1rem' }}>
            Upgrades
            </h3>
            <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
            }}>
            {Object.entries(UPGRADES).map(([key, upgrade]) => {
                const level = stats[`${key}Level`];
                const maxLevel = upgrade.maxLevel;
                const cost = getUpgradeCost(level);
                const canAfford = stats.totalCoins >= cost;
                const isMaxed = level >= maxLevel;
                
                return (
                <div key={key} style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.875rem' }}>{upgrade.icon}</span>
                    <div>
                        <h4 style={{ fontWeight: 700, color: 'white', margin: 0 }}>{upgrade.name}</h4>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                        Level {level}/{maxLevel}
                        </p>
                    </div>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.75rem' }}>
                    {upgrade.desc}
                    </p>
                    
                    {/* Progress bar */}
                    <div style={{
                    height: '0.5rem',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '9999px',
                    marginBottom: '0.75rem',
                    overflow: 'hidden'
                    }}>
                    <div style={{
                        height: '100%',
                        background: 'linear-gradient(to right, #60a5fa, #22d3ee)',
                        transition: 'all 0.3s',
                        width: `${(level / maxLevel) * 100}%`
                    }} />
                    </div>
                    
                    {!isMaxed ? (
                    <button
                        onClick={() => buyUpgrade(key)}
                        disabled={!canAfford}
                        style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        border: 'none',
                        cursor: canAfford ? 'pointer' : 'not-allowed',
                        backgroundColor: canAfford ? '#22c55e' : '#475569',
                        color: canAfford ? 'white' : '#94a3b8',
                        transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => canAfford && (e.target.style.backgroundColor = '#4ade80')}
                        onMouseLeave={(e) => canAfford && (e.target.style.backgroundColor = '#22c55e')}
                    >
                        🪙 {cost}
                    </button>
                    ) : (
                    <div style={{
                        width: '100%',
                        padding: '0.5rem',
                        backgroundColor: 'rgba(71, 85, 105, 0.5)',
                        borderRadius: '0.5rem',
                        textAlign: 'center',
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.5)'
                    }}>
                        MAXED
                    </div>
                    )}
                </div>
                );
            })}
            </div>
            
            {/* Planes */}
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'white', marginBottom: '1rem' }}>
            Paper Planes
            </h3>
            <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
            }}>
            {Object.entries(PLANES).map(([id, plane]) => {
                const owned = stats.unlockedPlanes.includes(id);
                const selected = stats.selectedPlane === id;
                const canAfford = stats.totalCoins >= plane.cost;
                
                return (
                <div 
                    key={id} 
                    onClick={() => buyPlane(id)}
                    style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    border: `2px solid ${selected ? '#4ade80' : 'transparent'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => !selected && (e.target.style.borderColor = 'rgba(255,255,255,0.3)')}
                    onMouseLeave={(e) => !selected && (e.target.style.borderColor = 'transparent')}
                >
                    <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                    }}>
                    <h4 style={{ fontWeight: 700, color: 'white', margin: 0 }}>{plane.name}</h4>
                    {selected && <span style={{ color: '#4ade80', fontSize: '0.875rem' }}>✓ Selected</span>}
                    </div>
                    
                    {/* Plane preview */}
                    <div style={{
                    height: '4rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '0.75rem'
                    }}>
                    <div style={{ 
                        width: '4rem',
                        height: '2rem',
                        transform: 'rotate(12deg)',
                        background: plane.color,
                        clipPath: 'polygon(100% 50%, 0% 0%, 20% 50%, 0% 100%)',
                    }} />
                    </div>
                    
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem' }}>
                    {plane.description}
                    </p>
                    
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.75rem' }}>
                    <div>Lift: {'★'.repeat(Math.round(plane.lift * 3))}{'☆'.repeat(3 - Math.round(plane.lift * 3))}</div>
                    <div>Speed: {'★'.repeat(Math.round(plane.drag * 3))}{'☆'.repeat(3 - Math.round(plane.drag * 3))}</div>
                    <div>Boost: {'★'.repeat(Math.round(plane.boost * 3))}{'☆'.repeat(3 - Math.round(plane.boost * 3))}</div>
                    </div>
                    
                    {!owned && (
                    <div style={{
                        textAlign: 'center',
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                        fontWeight: 700,
                        backgroundColor: canAfford ? 'rgba(234, 179, 8, 0.3)' : '#475569',
                        color: canAfford ? '#fde047' : '#94a3b8'
                    }}>
                        🪙 {plane.cost}
                    </div>
                    )}
                </div>
                );
            })}
            </div>
        </div>
        </div>
    );
    };

    const GameOverScreen = () => (
    <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 50
    }}>
        <div style={{
        backgroundColor: '#1e293b',
        borderRadius: '1.5rem',
        padding: '2rem',
        maxWidth: '28rem',
        width: '100%',
        margin: '0 1rem',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}>
        {lastRun.newBest && (
            <div style={{
            background: 'linear-gradient(to right, #eab308, #f97316)',
            color: 'white',
            fontWeight: 700,
            padding: '0.5rem 1rem',
            borderRadius: '9999px',
            textAlign: 'center',
            marginBottom: '1rem',
            animation: 'pulse 2s infinite'
            }}>
            🏆 NEW RECORD!
            </div>
        )}
        
        <h2 style={{
            fontSize: '1.875rem',
            fontWeight: 900,
            color: 'white',
            textAlign: 'center',
            marginBottom: '1.5rem'
        }}>
            FLIGHT LOG
        </h2>
        
        <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem'
        }}>
            <div style={{
            backgroundColor: 'rgba(51, 65, 85, 0.5)',
            borderRadius: '0.75rem',
            padding: '1rem',
            textAlign: 'center'
            }}>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Distance</div>
            <div style={{ fontSize: '1.875rem', fontWeight: 700, color: '#60a5fa' }}>{lastRun.distance}m</div>
            </div>
            <div style={{
            backgroundColor: 'rgba(51, 65, 85, 0.5)',
            borderRadius: '0.75rem',
            padding: '1rem',
            textAlign: 'center'
            }}>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Coins</div>
            <div style={{ fontSize: '1.875rem', fontWeight: 700, color: '#facc15' }}>+{lastRun.coins}</div>
            </div>
        </div>
        {isAuthenticated && scoreSubmitted && submissionResult && (
            <div style={{
                backgroundColor: 'rgba(139, 92, 246, 0.2)',
                border: '2px solid rgba(139, 92, 246, 0.5)',
                padding: '1rem',
                borderRadius: '0.75rem',
                marginBottom: '1rem'
            }}>
                <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
                fontSize: '0.875rem'
                }}>
                <span style={{ color: '#cbd5e1' }}>XP Earned</span>
                <span style={{ color: '#fbbf24', fontWeight: 700 }}>
                    +{submissionResult.rewards.xp}
                </span>
                </div>
                <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.875rem'
                }}>
                <span style={{ color: '#cbd5e1' }}>Coins Earned</span>
                <span style={{ color: '#fbbf24', fontWeight: 700 }}>
                    +{submissionResult.rewards.coins}
                </span>
                </div>
                {submissionResult.isNewHighScore && (
                <div style={{
                    marginTop: '0.5rem',
                    color: '#fbbf24',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textAlign: 'center',
                    animation: 'pulse 2s infinite'
                }}>
                    🎉 NEW PERSONAL BEST!
                </div>
                )}
            </div>
            )}

            {/* Not Logged In Warning */}
            {!isAuthenticated && !isLoading && (
            <div style={{
                backgroundColor: 'rgba(251, 191, 36, 0.2)',
                border: '2px solid #f59e0b',
                padding: '0.75rem',
                borderRadius: '0.75rem',
                marginBottom: '1rem'
            }}>
                <p style={{ 
                color: '#fef3c7', 
                fontSize: '0.75rem', 
                fontWeight: 600,
                marginBottom: '0.5rem'
                }}>
                ⚠️ Score not saved
                </p>
                <a
                href={`${config.authBaseUrl}/login`}
                style={{
                    display: 'block',
                    backgroundColor: '#fbbf24',
                    color: '#1e293b',
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textDecoration: 'none'
                }}
                >
                Login to Save Progress
                </a>
            </div>
            )}
        
        <div style={{
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '0.75rem',
            padding: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: '#c084fc' }}>🏅</span>
            <span style={{ color: '#cbd5e1' }}>Personal Best</span>
            </div>
            <span style={{ fontWeight: 700, color: 'white' }}>{stats.bestDistance}m</span>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button
            onClick={() => setGameState('playing')}
            style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(to right, #3b82f6, #06b6d4)',
                borderRadius: '0.75rem',
                fontWeight: 700,
                fontSize: '1.25rem',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'linear-gradient(to right, #60a5fa, #22d3ee)'}
            onMouseLeave={(e) => e.target.style.background = 'linear-gradient(to right, #3b82f6, #06b6d4)'}
            >
            🔄 Launch Again
            </button>
            
            <div style={{ display: 'flex', gap: '0.75rem' }}>
            {/* <button
                onClick={() => setGameState('shop')}
                style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#334155',
                borderRadius: '0.75rem',
                fontWeight: 700,
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#475569'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#334155'}
            >
                🛒 Shop
            </button> */}
            <button
                onClick={() => setGameState('menu')}
                style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#334155',
                borderRadius: '0.75rem',
                fontWeight: 700,
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#475569'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#334155'}
            >
                🏠 Menu
            </button>
            </div>
        </div>
        </div>
        
        <style>{`
        @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        `}</style>
    </div>
    );

    const HUD = () => (
    <div style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        padding: '1rem'
    }}>
        {/* Top HUD */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(12px)',
            padding: '0.75rem 1.5rem',
            borderRadius: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <div>
                <div style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase'
                }}>Distance</div>
                <div style={{
                fontSize: '1.5rem',
                fontFamily: 'monospace',
                fontWeight: 700,
                color: 'white'
                }}>{hudData.distance}m</div>
            </div>
            <div style={{ height: '2rem', width: '1px', backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
            <div>
                <div style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase'
                }}>Coins</div>
                <div style={{
                fontSize: '1.5rem',
                fontFamily: 'monospace',
                fontWeight: 700,
                color: '#facc15'
                }}>{hudData.coins}</div>
            </div>
            {hudData.combo > 1 && (
                <>
                <div style={{ height: '2rem', width: '1px', backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
                <div style={{
                    color: '#fb923c',
                    fontWeight: 700,
                    animation: 'pulse 1s infinite'
                }}>
                    {hudData.combo}x COMBO!
                </div>
                </>
            )}
            </div>
        </div>
        
        <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(12px)',
            padding: '0.5rem 1rem',
            borderRadius: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
            <div style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase'
            }}>Alt</div>
            <div style={{
            fontSize: '1.25rem',
            fontFamily: 'monospace',
            color: 'white'
            }}>{hudData.altitude}m</div>
        </div>
        </div>
        
        {/* Bottom HUD */}
        <div style={{
        position: 'absolute',
        bottom: '1rem',
        left: '1rem',
        right: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end'
        }}>
        <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(4px)',
            padding: '0.75rem',
            borderRadius: '0.75rem',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.875rem'
        }}>
            <p style={{ margin: '0 0 0.25rem 0' }}>↑↓ Pitch</p>
            <p style={{ margin: '0 0 0.25rem 0' }}>←→ Roll</p>
            <p style={{ margin: 0 }}>Space Boost</p>
        </div>
        
        {/* Boost Meter */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#67e8f9',
            fontWeight: 700,
            fontSize: '0.875rem'
            }}>
            ⚡ BOOST
            </div>
            <div style={{
            width: '12rem',
            height: '1.25rem',
            backgroundColor: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '9999px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            overflow: 'hidden'
            }}>
            <div style={{
                height: '100%',
                background: 'linear-gradient(to right, #2563eb, #22d3ee)',
                transition: 'width 0.1s',
                width: `${hudData.boost}%`
            }} />
            </div>
        </div>
        </div>
        
        {/* Center reticle */}
        <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '1.5rem',
        height: '1.5rem'
        }}>
        <div style={{
            width: '100%',
            height: '100%',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '9999px'
        }} />
        <div style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            width: '0.5rem',
            height: '2px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            transform: 'translateY(-50%)'
        }} />
        <div style={{
            position: 'absolute',
            top: '50%',
            right: 0,
            width: '0.5rem',
            height: '2px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            transform: 'translateY(-50%)'
        }} />
        <div style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            width: '2px',
            height: '0.5rem',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            transform: 'translateX(-50%)'
        }} />
        <div style={{
            position: 'absolute',
            left: '50%',
            bottom: 0,
            width: '2px',
            height: '0.5rem',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            transform: 'translateX(-50%)'
        }} />
        </div>
        
        {/* Notifications */}
        <div style={{
        position: 'absolute',
        top: '25%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.5rem'
        }}>
        {notifications.map(n => (
            <div 
            key={n.id}
            style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '9999px',
                fontWeight: 700,
                color: 'white',
                animation: 'bounceIn 0.3s ease-out',
                background: n.type === 'milestone' ? 'linear-gradient(to right, #eab308, #f97316)' :
                        n.type === 'powerup' ? 'linear-gradient(to right, #22c55e, #10b981)' :
                        n.type === 'zone' ? 'linear-gradient(to right, #a855f7, #ec4899)' :
                        n.type === 'warning' ? 'linear-gradient(to right, #ef4444, #f97316)' :
                        '#334155'
            }}
            >
            {n.text}
            </div>
        ))}
        </div>
        
        <style>{`
        @keyframes bounceIn {
            0% { transform: translateY(-20px) scale(0.8); opacity: 0; }
            50% { transform: translateY(5px) scale(1.05); }
            100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        `}</style>
    </div>
    );

    // ============================================
    // MAIN RENDER
    // ============================================

    return (
    <div style={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: '#0f172a',
        color: 'white',
        userSelect: 'none'
    }}>
        {/* Game Canvas */}
        {gameState === 'playing' && (
        <>
            <canvas ref={canvasRef} style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%'
            }} />
            <HUD />
        </>
        )}
        
        {/* UI Screens */}
        {gameState === 'menu' && <MainMenu />}
        {gameState === 'shop' && <Shop />}
        {gameState === 'gameOver' && <GameOverScreen />}
    </div>
    );
};

export default PaperFlightGame;